import { useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { formatTime } from '../../../utils/time';
import styles from '../CoinBackWidget.module.scss';

type CoinBackTimerProps = {
  timer: string;
  className?: string;
  isBoosterActive?: boolean;
  showDefault?: boolean;
  showFullTimer?: boolean;
  onClick?: () => void;
  onCompleted?: () => void;
};

const DEFAULT_TIMER = '00:00:00';

const CoinBackTimer = ({
  timer: destinationTime,
  className,
  isBoosterActive,
  showDefault,
  showFullTimer,
  onClick,
  onCompleted,
}: CoinBackTimerProps) => {
  const { t } = useTranslation();
  const getTimeLeft = useCallback(() => dayjs(destinationTime).diff(dayjs(), 'seconds'), [destinationTime]);

  const timeLeft = getTimeLeft();

  const timerFormatFunction = useMemo(() => (seconds: number) => formatTime(seconds, 's', t), [t]);

  const [timer, setTimer] = useState<string>(
    showDefault || getTimeLeft() <= 0 ? DEFAULT_TIMER : timerFormatFunction(timeLeft),
  );

  useEffect(() => {
    if (showDefault) return;

    const interval = setInterval(() => {
      const secondsToDate = getTimeLeft();

      if (secondsToDate <= 0) {
        setTimer(DEFAULT_TIMER);
        clearInterval(interval);
        if (onCompleted) {
          onCompleted();
        }
      } else {
        const timerFormatted = timerFormatFunction(secondsToDate);
        setTimer(timerFormatted);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [getTimeLeft, showFullTimer, timerFormatFunction, onCompleted, showDefault]);

  return (
    <div
      className={classNames(styles.coinBackTimer, className, {
        [styles.boosterActive]: isBoosterActive,
      })}
      onClick={onClick}
    >
      {timer}
    </div>
  );
};

export default CoinBackTimer;
